import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { LifeBuoy, Send, Loader2, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = { bug: 'Problem / Bug', question: 'Question', feature: 'Feature Request', feedback: 'Feedback' };
const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
];
const STATUS_BADGE = {
  open: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  in_progress: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

export default function AdminSupport() {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };

  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchRequests = async (f = filter) => {
    try {
      const res = await axios.get(`${API}/support/admin/requests?status_filter=${f}`, { headers });
      setRequests(res.data.requests);
      setCounts(res.data.counts || {});
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, [token, filter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/support/admin/requests/${id}`, { status }, { headers });
      toast({ title: `Marked as ${status.replace('_', ' ')}` });
      fetchRequests();
    } catch { toast({ title: 'Failed to update status', variant: 'destructive' }); }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/admin/requests/${id}/reply`, { message: replyText }, { headers });
      toast({ title: 'Reply sent — user notified by email' });
      setReplyText('');
      fetchRequests();
    } catch (err) {
      toast({ title: 'Failed to send reply', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-support-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-600/20 flex items-center justify-center">
          <LifeBuoy className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Support &amp; Feedback</h1>
          <p className="text-sm text-gray-500">User help requests and feedback — reply and manage status here.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {STATUSES.map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
            data-testid={`support-filter-${s.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === s.key ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40' : 'text-gray-400 border-white/10 hover:text-gray-200'}`}>
            {s.label} {s.key === 'all' ? (total ? `(${total})` : '') : (counts[s.key] ? `(${counts[s.key]})` : '')}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Loading...</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center" data-testid="admin-no-requests">No support requests {filter !== 'all' ? `with status "${filter}"` : 'yet'}.</p>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} className="bg-[#132218] border border-emerald-900/30 rounded-lg" data-testid={`admin-support-request-${r.id}`}>
              <button onClick={() => { setExpanded(expanded === r.id ? null : r.id); setReplyText(''); }} className="w-full flex items-center gap-3 p-4 text-left">
                <div className="w-8 h-8 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-300 text-xs font-bold flex-shrink-0">
                  {(r.user_name || r.user_email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{r.subject}</p>
                  <p className="text-xs text-gray-500 truncate">{r.user_name || r.user_email} &bull; {CATEGORY_LABELS[r.category] || r.category} &bull; {new Date(r.created_at).toLocaleString()}</p>
                </div>
                {r.replies?.length > 0 && <span className="flex items-center gap-1 text-xs text-emerald-400"><MessageSquare className="w-3.5 h-3.5" /> {r.replies.length}</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_BADGE[r.status] || STATUS_BADGE.open}`}>
                  {(r.status || 'open').replace('_', ' ')}
                </span>
                {expanded === r.id ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>

              {expanded === r.id && (
                <div className="px-4 pb-4 space-y-3 border-t border-emerald-900/30 pt-3">
                  <p className="text-xs text-gray-500">{r.user_email}</p>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{r.message}</p>

                  {(r.replies || []).map(rep => (
                    <div key={rep.id} className="bg-emerald-600/10 border border-emerald-500/20 rounded-lg p-3">
                      <p className="text-[11px] font-semibold text-emerald-400 mb-1">You replied &bull; {new Date(rep.created_at).toLocaleString()}</p>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{rep.message}</p>
                    </div>
                  ))}

                  <div className="flex gap-2">
                    <textarea
                      rows={2}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Write a reply — the user is notified by email..."
                      className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-600 resize-none"
                      data-testid={`admin-reply-input-${r.id}`}
                    />
                    <button onClick={() => sendReply(r.id)} disabled={sending || !replyText.trim()}
                      className="px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-medium flex items-center gap-2 transition-colors"
                      data-testid={`admin-reply-send-${r.id}`}>
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="flex gap-2">
                    {r.status !== 'in_progress' && (
                      <button onClick={() => updateStatus(r.id, 'in_progress')} className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors" data-testid={`mark-in-progress-${r.id}`}>
                        Mark In Progress
                      </button>
                    )}
                    {r.status !== 'resolved' && (
                      <button onClick={() => updateStatus(r.id, 'resolved')} className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors" data-testid={`mark-resolved-${r.id}`}>
                        Mark Resolved
                      </button>
                    )}
                    {r.status === 'resolved' && (
                      <button onClick={() => updateStatus(r.id, 'open')} className="text-xs px-3 py-1.5 rounded-full border border-blue-500/30 text-blue-300 hover:bg-blue-500/10 transition-colors" data-testid={`reopen-${r.id}`}>
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
